import { useState, useRef, useEffect } from 'react';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputTestId?: string;
  /**
   * When true, the bar fills its parent (no built-in max-width cap) so the
   * caller controls the width via a wrapper. Default `false` keeps the
   * standard responsive widths used across most tables.
   */
  fluid?: boolean;
  /**
   * Disables the whole bar (input and icon buttons). The enable/disable rule
   * (e.g. list still doing its initial load) is owned by the caller.
   */
  disabled?: boolean;
}

export function SearchBar({
  value,
  onChange,
  placeholder = 'Szukaj po nazwie',
  inputTestId,
  fluid = false,
  disabled = false,
}: SearchBarProps) {
  const [isPressed, setIsPressed] = useState(false);
  const [showFocusRing, setShowFocusRing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wasFocusedRef = useRef(false);
  const isKeyboardNavigationRef = useRef(false);

  // Track keyboard navigation globally
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Detect Tab key for keyboard navigation
      if (e.key === 'Tab') {
        isKeyboardNavigationRef.current = true;
      }
    };

    const handleDocumentMouseDown = () => {
      // Reset keyboard navigation flag on mouse interaction
      isKeyboardNavigationRef.current = false;
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, []);

  // Track if input was focused before re-render (only when value changes)
  useEffect(() => {
    if (document.activeElement === inputRef.current) {
      wasFocusedRef.current = true;
    }
  }, [value]);

  // Restore focus after re-render if it was focused before (only when value changes)
  useEffect(() => {
    if (wasFocusedRef.current && inputRef.current) {
      // Use requestAnimationFrame to ensure this happens after render
      requestAnimationFrame(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
        }
        wasFocusedRef.current = false;
      });
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/^\s+/, '');

    if (newValue.length > 200 || newValue === value) {
      return;
    }

    onChange(newValue);
  };

  const handleInputFocus = () => {
    // Only show focus ring if focus came from keyboard navigation
    if (isKeyboardNavigationRef.current) {
      setShowFocusRing(true);
    }
  };

  const handleInputBlur = () => {
    setShowFocusRing(false);
    isKeyboardNavigationRef.current = false;
  };

  const handleMouseDown = () => {
    setIsPressed(true);
    // Ensure focus ring is hidden on mouse interaction
    setShowFocusRing(false);
  };

  const handleMouseUp = () => {
    setIsPressed(false);
  };

  const handleMouseLeave = () => {
    setIsPressed(false);
  };

  const handleClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange('');
  };

  const handleSearchClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // Focus the input when search icon is clicked
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div
      className={`h-10 ${fluid ? 'w-full' : 'max-w-[312px] sm:w-[280px] md:w-[400px] lg:w-[500px] xl:w-[720px]'} bg-[#ECEEEF] rounded-lg flex items-center gap-1 relative overflow-hidden transition-all duration-200 ease-in-out ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#E0E2E4]'} ${showFocusRing ? 'ring-2 ring-[#2A4455]' : ''}`}
      data-testid="search-bar-container"
    >
      {/* Inset shadow overlay - only on left half */}
      {isPressed && (
        <div
          className="absolute left-0 top-0 bottom-0 z-0 pointer-events-none"
          style={{
            width: '50%',
            boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.1)',
            backgroundColor: '#181A1B1A',
            borderRadius: '0 45px 0px 0',
          }}
        />
      )}
      <div
        className="flex-1 px-5 flex items-center gap-2.5 relative z-10"
        onMouseDown={disabled ? undefined : handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          maxLength={200}
          tabIndex={0}
          disabled={disabled}
          className={`flex-1 ${fluid ? '' : 'max-w-[242px]'} bg-transparent outline-none focus:outline-none text-[#181A1B] text-sm leading-5 tracking-[0.25px] cursor-text relative z-10 placeholder:text-transparent`}
          data-testid={inputTestId ?? 'search-bar-input'}
          aria-label="Szukaj administratorów"
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        />
        {!value && (
          <span
            className="absolute inset-y-0 left-5 right-0 flex items-center pointer-events-none"
            aria-hidden="true"
          >
            <span
              className={`${fluid ? 'max-w-full' : 'max-w-[242px]'} overflow-hidden text-ellipsis whitespace-nowrap text-[#474A4C] text-sm leading-5 tracking-[0.25px]`}
            >
              {placeholder}
            </span>
          </span>
        )}
      </div>

      {/* Search/Clear Button */}
      {value ? (
        <button
          onClick={handleClearClick}
          disabled={disabled}
          className="absolute right-1 flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ease-in-out hover:bg-[#181A1B14] hover:disabled:bg-transparent disabled:cursor-not-allowed z-10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
          aria-label="Wyczyść wyszukiwanie"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z"
              fill="#474A4C"
            />
          </svg>
        </button>
      ) : (
        <button
          onClick={handleSearchClick}
          type="button"
          disabled={disabled}
          className="absolute right-1 flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ease-in-out hover:bg-[#181A1B14] hover:disabled:bg-transparent disabled:cursor-not-allowed z-10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
          aria-label="Szukaj"
          onKeyDown={(e) => {
            // When Enter or Space is pressed, focus the input
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              if (inputRef.current) {
                inputRef.current.focus();
                isKeyboardNavigationRef.current = true;
              }
            }
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
              stroke="#181A1B"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M21 21.0004L16.65 16.6504"
              stroke="#181A1B"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
